import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { FaMoneyBillWave, FaBolt, FaUser, FaPhone } from 'react-icons/fa';
import SecureServiceBase from './SecureServiceBase';

const SecurePPIDMTModal = ({ show, onHide, onSuccess }) => {
  const [recipientName, setRecipientName] = useState('');
  const [recipientMobile, setRecipientMobile] = useState('');
  const [amount, setAmount] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [transferType, setTransferType] = useState('bank'); // bank or wallet

  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000];

  const handleAmountChange = (val, validateAmount) => {
    setAmount(val);
    if (validateAmount) {
      validateAmount(val);
    }
  };

  const handleSubmit = (e, submitHandler) => {
    e.preventDefault();
    
    if (!recipientName || !recipientMobile || !amount) {
      return;
    }

    if (transferType === 'bank' && (!bankAccount || !ifscCode)) {
      return;
    }

    const formData = {
      recipient_name: recipientName,
      recipient_mobile: recipientMobile,
      amount: Number(amount),
      transfer_type: transferType,
      bank_account: bankAccount,
      ifsc_code: ifscCode,
      type: 'dmt_transfer'
    };

    submitHandler(formData);
  };

  return (
    <SecureServiceBase
      show={show}
      onHide={onHide}
      onSuccess={onSuccess}
      serviceName="PPI DMT Transfer"
      serviceEndpoint="/api/dmt/process"
      requiresBalance={true}
      minAmount={100}
    >
      {({ balance, error, loading, validateAmount, handleSubmit, isFormValid }) => (
        <form onSubmit={(e) => handleSubmit(e, handleSubmit)}>
          {/* Transfer Type Selection */}
          <div className="mb-3">
            <label className="form-label fw-bold">Transfer Type</label>
            <div className="row g-2">
              <div className="col-md-6">
                <Card 
                  className={`h-100 cursor-pointer ${transferType === 'bank' ? 'border-primary' : ''}`}
                  onClick={() => setTransferType('bank')}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body className="text-center p-3">
                    <FaMoneyBillWave className="text-primary fs-2 mb-2" />
                    <h6 className="mb-1">Bank Transfer</h6>
                    <small className="text-muted">Transfer to bank account</small>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-6">
                <Card 
                  className={`h-100 cursor-pointer ${transferType === 'wallet' ? 'border-primary' : ''}`}
                  onClick={() => setTransferType('wallet')}
                  style={{ cursor: 'pointer' }}
                >
                  <Card.Body className="text-center p-3">
                    <FaPhone className="text-success fs-2 mb-2" />
                    <h6 className="mb-1">Wallet Transfer</h6>
                    <small className="text-muted">Transfer to mobile wallet</small>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </div>

          {/* Recipient Details */}
          <div className="mb-3">
            <label htmlFor="recipientName" className="form-label">
              Recipient Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="recipientName"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Enter recipient's full name"
              disabled={loading}
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="recipientMobile" className="form-label">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="tel"
              className="form-control"
              id="recipientMobile"
              value={recipientMobile}
              onChange={(e) => setRecipientMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="Enter 10-digit mobile number"
              maxLength="10"
              disabled={loading}
              required
            />
          </div>

          {/* Bank Details (only for bank transfer) */}
          {transferType === 'bank' && (
            <>
              <div className="mb-3">
                <label htmlFor="bankAccount" className="form-label">
                  Bank Account Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="bankAccount"
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter bank account number"
                  disabled={loading}
                  required
                />
              </div>

              <div className="mb-3">
                <label htmlFor="ifscCode" className="form-label">
                  IFSC Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="ifscCode"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                  placeholder="Enter IFSC code"
                  maxLength="11"
                  disabled={loading}
                  required
                />
              </div>
            </>
          )}

          {/* Amount Input */}
          <div className="mb-3">
            <label htmlFor="amount" className="form-label">
              Amount <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="number"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="amount"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value, validateAmount)}
                placeholder="Enter amount"
                min="100"
                step="1"
                disabled={loading}
                required
              />
            </div>
            {error && (
              <div className="invalid-feedback d-block">
                {error}
              </div>
            )}
            {balance !== null && amount && !error && (
              <div className="form-text text-success">
                Remaining balance: ₹{(balance - Number(amount)).toFixed(2)}
              </div>
            )}
          </div>

          {/* Quick Amount Selection */}
          <div className="mb-3">
            <label className="form-label">Quick Amounts</label>
            <div className="d-flex flex-wrap gap-2">
              {quickAmounts.map((quickAmount) => (
                <Button
                  key={quickAmount}
                  variant={amount === quickAmount.toString() ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => handleAmountChange(quickAmount.toString(), validateAmount)}
                  disabled={loading}
                >
                  ₹{quickAmount}
                </Button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-grid">
            <Button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid || !recipientName || !recipientMobile || !amount || (transferType === 'bank' && (!bankAccount || !ifscCode))}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </span>
                  Processing...
                </>
              ) : (
                <>
                  <FaBolt className="me-2"></FaBolt>
                  Transfer ₹{amount || '0'}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </SecureServiceBase>
  );
};

export default SecurePPIDMTModal;
